import { StatusBadgeTone } from "../../enums/status-badge-tone.enum";
import { SessionTemplateBlock } from "./session-template-block.model";
import { StoreStatus } from "./store-status.enum";


export enum AudienceType {
  Adults = "adults",
  Kids = "kids",
  General = "general"
}

export enum TargetGender {
  Male = "male",
  Female = "female",
  NonBinary = "non_binary",
  Everyone = "everyone"
}

/** `PublishedForTest` is left out: it is never offered as a choice in the UI. */
// Labels are translation keys of the root `shared.*` namespace; render them with the transloco pipe.
export const storeStatusOptions = [
  { value: StoreStatus.Draft, label: 'shared.storeStatus.draft' },
  { value: StoreStatus.Published, label: 'shared.storeStatus.published' },
  { value: StoreStatus.Archived, label: 'shared.storeStatus.archived' }
];

export const audienceTypeOptions = [
  { value: AudienceType.Adults, label: 'shared.audienceType.adults' },
  { value: AudienceType.Kids, label: 'shared.audienceType.kids' },
  { value: AudienceType.General, label: 'shared.audienceType.general' }
];

export const targetGenderOptions = [
  { value: TargetGender.Male, label: 'shared.targetGender.male' },
  { value: TargetGender.Female, label: 'shared.targetGender.female' },
  { value: TargetGender.NonBinary, label: 'shared.targetGender.non_binary' },
  { value: TargetGender.Everyone, label: 'shared.targetGender.everyone' }
];

/** Values are translation keys under the root `shared` section: render them with the `transloco` pipe or `TranslocoService.translate()`. */
export const universalOptions = [
  { value: true, label: 'shared.universal.universal' },
  { value: false, label: 'shared.universal.personalized' }
];

/** The translation key for 'Universal' or 'Personalized', the same wording the session template filters use. */
export function universalLabel(isUniversal: boolean): string {
  return universalOptions.find((option) => option.value === isUniversal)?.label ?? '';
}

export function universalBadgeTone(isUniversal: boolean): StatusBadgeTone {
  return isUniversal ? 'info' : 'neutral';
}

export type SessionFilter = {
  domains: number[];
  categories: number[];
  storeStatuses: StoreStatus[];
  audienceTypes: AudienceType[];
  targetGenders: TargetGender[];
  isUniversal?: boolean[];
  languages?: number[];
  sessionStoreStatuses?: StoreStatus[];
  /** Free text, matched against the title, the internal reference and the external id. */
  searchTerm?: string;
}

export type SessionTemplateTranslation = {
  languageId: number;
  title: string;
  subtitle: string;
  description: string;
  placeholderTitle: string;
  coreChoiceQuestion: string;
  coreChoiceConstraint: string;
  refinerQuestion: string;
  refinerConstraint: string;
}

export type SessionTemplateBase = {
  title: string;
  subtitle: string;
  description: string;
  placeholderTitle: string;
  estimatedDuration: number;
  sessionCategoryId: number;
  therapeuticDomainId: number;
  creator: string;
  coverUrl?: string;
  blocks: SessionTemplateBlock[];
  smartSurveyId?: number;
  coreChoiceQuestion?: string;
  coreChoiceConstraint?: string;
  refinerQuestion?: string;
  refinerConstraint?: string;
  leadingSilence: number;
  trailingSilence: number;
  audienceType: AudienceType;
  targetGender: TargetGender;
  isUniversal: boolean;
  /** Ordering weight between `MIN_SESSION_TEMPLATE_WEIGHT` and `MAX_SESSION_TEMPLATE_WEIGHT`; 1 is the heaviest. */
  weight: number;
  internalReference: string;
  isDirty?: boolean;
}

/**
 * The order session templates are listed in throughout the admin: by weight (1 first, as the
 * heaviest), then by title.
 */
export function compareSessionTemplates(
  a: { weight: number; title: string },
  b: { weight: number; title: string },
): number {
  return (a.weight - b.weight) || (a.title || '').localeCompare(b.title || '');
}

export const MIN_SESSION_TEMPLATE_WEIGHT = 1;
export const MAX_SESSION_TEMPLATE_WEIGHT = 100;
export const DEFAULT_SESSION_TEMPLATE_WEIGHT = 100;

export type SessionTemplate = SessionTemplateBase & {
  id: number;
  externalId: string; // a GUID that also acts a unique identifier, next to id
  originalLanguageId: number;
  storeStatus?: StoreStatus;
  updatedAt?: Date | null;
  createdAt?: Date;
  /** Number of the latest recorded structure of the template; 0 until one is recorded. */
  currentVersionNumber: number;
  readonly adminTitle: string;
}

export type SessionTemplateDetails = SessionTemplate & {
  isDetailed: boolean;
}